import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarClock, Settings2 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { usePermission } from '../../../core/auth/usePermission';
import { ApiError } from '../../../core/api-client';
import { VISUAL } from '../../../design-system/visual';
import { hrApi } from '../api';
import type { WeekSchedule, ExpectedShift } from '../types';
import { ShiftTemplatesPanel } from '../components/ShiftTemplatesPanel';
import { AssignShiftDialog } from '../components/AssignShiftDialog';
import { OverrideDialog } from '../components/OverrideDialog';
import { AttendanceSettingsPanel } from '../components/AttendanceSettingsPanel';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function mondayOf(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function shortDate(dateStr: string): string {
  const [, m, day] = dateStr.split('-');
  return `${m}/${day}`;
}

function formatShift(cell: ExpectedShift, onLeave: boolean): string {
  if (onLeave || cell.source === 'leave') return 'Leave';
  if (!cell.expected || !cell.start || !cell.end) return 'Off';
  const overnight = cell.end < cell.start;
  return `${cell.start} → ${cell.end}${overnight ? ' (+1)' : ''}`;
}

function cellClass(cell: ExpectedShift, onLeave: boolean, clickable: boolean): string {
  const base = 'w-full min-w-[112px] rounded-xl px-2.5 py-2 text-[12px] font-medium text-left ring-1 transition-colors';
  const interactive = clickable ? 'cursor-pointer hover:ring-2' : 'cursor-default';
  if (onLeave || cell.source === 'leave') {
    return [base, interactive, 'bg-lavender-100 text-lavender-700 ring-lavender-200 dark:bg-lavender-400/15 dark:text-lavender-200 dark:ring-lavender-400/30'].join(' ');
  }
  if (!cell.expected) {
    return [base, interactive, 'bg-muted/40 text-muted-foreground ring-border/40'].join(' ');
  }
  if (cell.source === 'override') {
    return [base, interactive, 'bg-coral/12 text-coral-700 ring-coral/30 dark:bg-coral/20 dark:text-coral-200 dark:ring-coral/40'].join(' ');
  }
  return [base, interactive, 'bg-lime-neon/15 text-ink ring-lime-neon/30 dark:bg-lime-neon/10 dark:text-lime-neon dark:ring-lime-neon/25'].join(' ');
}

interface OverrideTarget {
  employeeId: string;
  name: string;
  date: string;
  onLeave: boolean;
  current: ExpectedShift;
}

/** HR week schedule grid — who's expected to work, when, and why (template,
 *  override, default hours, or approved leave). Row action assigns a shift
 *  template; cell click opens a per-day override. View-only users (holding
 *  hr.schedule.view_all but not hr.schedule.manage) see the grid without
 *  any edit affordances. */
export function SchedulePage() {
  const canManage = usePermission('hr.schedule.manage');
  const canWriteSettings = usePermission('settings.write');

  // Only used to power the AttendanceSettingsPanel affordance inside the
  // off-state explainer (for users who can also write settings) — NOT the
  // source of truth for whether scheduling is on. A user with only
  // hr.schedule.view_all can legitimately 403 on this settings endpoint
  // while still holding view access to /schedule/week itself, so the
  // off-state explainer must never depend on this query succeeding. Gated
  // on settings.write so view-only users don't hit a guaranteed 403 on
  // every visit to this page (they never render the panel that reads it).
  const settingsQ = useApi<{ settings: { scheduling_enabled: boolean } }>(
    canWriteSettings ? '/api/hr/attendance/settings' : null,
  );

  const [weekStart, setWeekStart] = React.useState(() => mondayOf(new Date()));
  const [week, setWeek] = React.useState<WeekSchedule | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<number | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setStatus(null);
    hrApi.getWeekSchedule(weekStart)
      .then((res) => { if (!cancelled) { setWeek(res); setLoading(false); } })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err.message ?? 'Failed to load the schedule');
        setStatus(err instanceof ApiError ? err.status : null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [weekStart, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  // Authoritative off-state: derived from the /schedule/week response itself,
  // not the separate (possibly-403) attendance-settings fetch.
  const schedulingKnownOff = !loading && !error && week?.scheduling_enabled === false;

  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const [assignFor, setAssignFor] = React.useState<{ id: string; name: string } | null>(null);
  const [overrideTarget, setOverrideTarget] = React.useState<OverrideTarget | null>(null);

  if (loading) {
    return <LoadingState variant="page" />;
  }

  if (schedulingKnownOff) {
    return (
      <div>
        <PageHeader
          eyebrow="Team / Schedule"
          title="Schedule"
          description="Shift scheduling isn't turned on for this workspace yet."
        />
        <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-2xl">
          <section className={[VISUAL.bento, 'p-8'].join(' ')}>
            <EmptyState
              icon={<CalendarClock />}
              title="Shift scheduling is off"
              description="Turn it on from Attendance policy to assign shift templates and manage per-day overrides. Until then, everyone follows their standard hours."
              action={
                <Link to="/dashboard/hr/penalty-rules">
                  <Button variant="outline" className="rounded-full h-10 px-5 text-[13px] font-semibold">
                    Go to Attendance policy
                  </Button>
                </Link>
              }
            />
            {canWriteSettings && (
              <div className="mt-6 pt-6 border-t border-border/40">
                <AttendanceSettingsPanel />
                <div className="flex justify-end mt-3">
                  <Button variant="ghost" size="sm" className="rounded-full h-8 text-[12px]" onClick={() => { settingsQ.reload(); reload(); }}>
                    I turned it on — refresh
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Team / Schedule"
        title="Schedule"
        description="Who's expected to work, and when — templates, per-day overrides, and approved leave all resolve into one view."
        actions={
          canManage ? (
            <Button
              variant="outline"
              onClick={() => setTemplatesOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Shift templates
            </Button>
          ) : undefined
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => setWeekStart((w) => addDays(w, -7))}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setWeekStart(mondayOf(new Date()))}
              className="inline-flex items-center px-3.5 h-8 text-[12px] font-semibold rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              This week
            </button>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => setWeekStart((w) => addDays(w, 7))}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {week && (
            <p className="text-[12.5px] text-muted-foreground">
              {shortDate(week.days[0])} – {shortDate(week.days[6])}
            </p>
          )}
        </div>

        {loading ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <LoadingState variant="page" />
          </section>
        ) : status === 403 ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="The schedule is visible to workspace owners and HR managers." />
          </section>
        ) : error ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ErrorState title="Couldn't load the schedule" description={error} onRetry={reload} />
          </section>
        ) : !week || week.employees.length === 0 ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <EmptyState icon={<CalendarClock />} title="No employees yet" description="Invite your team from People to start scheduling shifts." />
          </section>
        ) : (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-muted/50 border-b border-border/60">
                    <th className="px-4 py-3 text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">Employee</th>
                    {week.days.map((d, i) => (
                      <th key={d} className="px-3 py-3 text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground whitespace-nowrap">
                        {DAY_LABELS[i]} <span className="normal-case font-normal text-muted-foreground/60">{shortDate(d)}</span>
                      </th>
                    ))}
                    {canManage && <th className="px-3 py-3" />}
                  </tr>
                </thead>
                <tbody>
                  {week.employees.map((emp) => (
                    <tr key={emp.employee_id} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap align-top">{emp.name}</td>
                      {emp.cells.map((cell, i) => {
                        const onLeave = !!emp.on_leave[i];
                        const date = week.days[i];
                        return (
                          <td key={date} className="px-2 py-2 align-top">
                            <button
                              type="button"
                              disabled={!canManage}
                              aria-label={`${emp.name} — ${date}`}
                              onClick={() => canManage && setOverrideTarget({ employeeId: emp.employee_id, name: emp.name, date, onLeave, current: cell })}
                              className={cellClass(cell, onLeave, canManage)}
                            >
                              {formatShift(cell, onLeave)}
                            </button>
                          </td>
                        );
                      })}
                      {canManage && (
                        <td className="px-3 py-2 text-right align-top">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-full h-8"
                            onClick={() => setAssignFor({ id: emp.employee_id, name: emp.name })}
                          >
                            Assign
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {canManage && (
        <>
          <Dialog
            open={templatesOpen}
            onOpenChange={(v) => {
              setTemplatesOpen(v);
              // Template edits (rename, day/time changes, soft-delete) happen
              // inside this dialog — reload the week grid on close so edited
              // or removed templates aren't shown stale.
              if (!v) reload();
            }}
          >
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Shift templates</DialogTitle>
              </DialogHeader>
              <ShiftTemplatesPanel />
            </DialogContent>
          </Dialog>

          {assignFor && (
            <AssignShiftDialog
              open
              onOpenChange={(v) => { if (!v) setAssignFor(null); }}
              employeeId={assignFor.id}
              employeeName={assignFor.name}
              onSaved={() => { setAssignFor(null); reload(); }}
            />
          )}

          {overrideTarget && (
            <OverrideDialog
              open
              onOpenChange={(v) => { if (!v) setOverrideTarget(null); }}
              employeeId={overrideTarget.employeeId}
              employeeName={overrideTarget.name}
              date={overrideTarget.date}
              onLeave={overrideTarget.onLeave}
              current={overrideTarget.current}
              onSaved={() => { setOverrideTarget(null); reload(); }}
            />
          )}
        </>
      )}
    </div>
  );
}
